import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { signOut } from 'firebase/auth';
import { doc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../../lib/firebase';
import {
  Building2,
  Bell,
  User,
  ChevronDown,
  LogOut,
  Menu,
  MessageCircle,
} from 'lucide-react';
import { NotificationDropdown } from '../NotificationDropdown';
import { useNotifications } from '../../hooks/useNotifications';
import { translations } from '../../translations/layout';
import { motion, AnimatePresence } from 'framer-motion';

interface NavbarProps {
  onMenuClick: () => void;
  isSidebarCollapsed: boolean;
}

export function Navbar({ onMenuClick, isSidebarCollapsed }: NavbarProps) {
  const navigate = useNavigate();
  const { user, setUser } = useAuthStore();
  const { unreadCount } = useNotifications();
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = async () => {
    try {
      if (user?.id) {
        const userRef = doc(db, 'users', user.id);
        await updateDoc(userRef, {
          isOnline: false,
          lastSeen: new Date()
        });
      }
      await signOut(auth);
      setUser(null);
      navigate('/auth', { replace: true });
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  return (
    <header
      className={`fixed top-0 right-0 z-40 transition-all duration-500 ${
        isSidebarCollapsed ? 'md:left-20' : 'md:left-72'
      } left-0`}
    >
      <div
        className={`mx-3 mt-3 rounded-2xl transition-all duration-500 ${
          scrolled
            ? 'bg-white/85 backdrop-blur-2xl shadow-glass border border-slate-200/50'
            : 'bg-white/40 backdrop-blur-md border border-white/60'
        }`}
      >
        <div className="px-4 sm:px-5">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-3 md:hidden">
              <button
                onClick={onMenuClick}
                className="p-2 rounded-xl text-slate-500 hover:bg-white/80 hover:text-slate-700 active:scale-95 transition-all"
              >
                <Menu className="h-5 w-5" />
              </button>
              <Link to="/" className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-600 to-primary-700 flex items-center justify-center shadow-sm shadow-primary-600/20">
                  <Building2 className="h-4 w-4 text-white" />
                </div>
                <span className="text-base font-bold text-slate-900 tracking-tight">Expo 1</span>
              </Link>
            </div>

            <div className="hidden md:block">
              <h2 className="text-sm font-medium text-slate-400 tracking-wide">
                Conseil de Surveillance
              </h2>
            </div>

            <div className="flex-1" />

            <div className="flex items-center gap-1 sm:gap-1.5">
              <button
                onClick={() => navigate('/messages')}
                className="relative p-2.5 sm:p-2.5 rounded-xl text-slate-400 hover:bg-white/80 hover:text-slate-600 active:scale-95 transition-all"
              >
                <MessageCircle className="h-5 w-5 sm:h-[18px] sm:w-[18px]" />
              </button>

              <div className="relative">
                <button
                  onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
                  className="relative p-2.5 sm:p-2.5 rounded-xl text-slate-400 hover:bg-white/80 hover:text-slate-600 active:scale-95 transition-all"
                >
                  <Bell className="h-5 w-5 sm:h-[18px] sm:w-[18px]" />
                  {unreadCount > 0 && (
                    <span className="absolute top-1.5 right-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white ring-2 ring-white animate-scale-in">
                      {unreadCount}
                    </span>
                  )}
                </button>

                <AnimatePresence>
                  {isNotificationsOpen && (
                    <NotificationDropdown onClose={() => setIsNotificationsOpen(false)} />
                  )}
                </AnimatePresence>
              </div>

              <div className="hidden md:block w-px h-5 bg-slate-200/60 mx-2" />

              <div className="relative hidden md:block">
                <button
                  onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                  className="flex items-center gap-2.5 pl-2 pr-3 py-1.5 rounded-xl hover:bg-white/80 active:scale-[0.98] transition-all"
                >
                  <div className="relative">
                    <img
                      className="h-8 w-8 rounded-lg object-cover ring-1 ring-slate-200/60"
                      src={`https://ui-avatars.com/api/?name=${user?.firstName}+${user?.lastName}&background=338bff&color=fff&bold=true&format=svg`}
                      alt=""
                    />
                    <div className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-accent-500 border-2 border-white" />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-semibold text-slate-700 leading-none">
                      {user?.firstName}
                    </p>
                  </div>
                  <ChevronDown className={`h-3.5 w-3.5 text-slate-300 transition-transform duration-200 ${isProfileMenuOpen ? 'rotate-180' : ''}`} />
                </button>

                <AnimatePresence>
                  {isProfileMenuOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 6, scale: 0.97 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 6, scale: 0.97 }}
                      transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
                      className="absolute right-0 mt-2 w-52 rounded-2xl bg-white/90 backdrop-blur-xl shadow-elevated border border-slate-200/60 py-1.5 overflow-hidden"
                    >
                      <Link
                        to="/profile"
                        onClick={() => setIsProfileMenuOpen(false)}
                        className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-slate-600 hover:bg-slate-50 hover:text-slate-800 transition-colors"
                      >
                        <User className="h-4 w-4 text-slate-400" />
                        Mon profil
                      </Link>
                      <div className="mx-3 my-1 border-t border-slate-100" />
                      <button
                        onClick={handleLogout}
                        className="flex w-full items-center gap-2.5 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 hover:text-red-600 transition-colors"
                      >
                        <LogOut className="h-4 w-4" />
                        Deconnexion
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
